import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import RootLayout from './components/layouts/RootLayout';
import Waivers from './components/views/Waivers';
import Login from './components/views/Login';
import RfidLogin from './components/views/RfidLogin';
import Calls from './components/views/Calls';
import Welcome from './components/views/Welcome';
import Bookings from './components/views/Bookings';
import BookingSearchView from './components/views/BookingSearchView';
import PizzaView from './components/views/PizzaView';
import VoucherSearch from './components/views/VoucherSearch';
import Resources from './components/views/Resources';
import RequireAuth from './util/requireAuth';
import SMS from './components/views/SMS';
import Statistics from './components/views/Statistics';
import BriefingView from './components/views/BriefingView';
import RequestPasswordReset from './components/views/RequestPasswordReset';
import ResetPassword from './components/views/ResetPassword';
import StoredRequestsView from './components/views/StoredRequestsView';
import { EmailView } from './components/views/EmailView';
import CustomerManagement from './components/views/CustomerManagement';
import UserManagement from './components/views/UserManagement';
import MemberManagement from './components/views/MemberManagement';
import PlayerNameBlacklistManagement from './components/views/PlayerNameBlacklistManagement';
import EntertainmentManagement from './components/views/EntertainmentManagement';
import GetResponseMarketingManagement from './components/views/GetResponseMarketingManagement';
import OnlineCustomerManagement from './components/views/OnlineCustomerManagement';
import ProductManagement from './components/views/ProductManagement';
import PriceOverview from './components/views/PriceOverview';
import BookingWidgetManagement from './components/views/BookingWidgetManagement';
import GroupManagement from './components/views/GroupManagement';
import UpgradeManagement from './components/views/UpgradeManagement';
import MenuItems from './components/views/MenuItems';
import BranchManagement from './components/views/BranchManagement';
import LegalEntityManagement from './components/views/LegalEntityManagement';
import EntertainmentTypeManagement from './components/views/EntertainmentTypeManagement';
import CopagoGroupManagement from './components/views/CopagoGroupManagement';
import CopagoArticleManagement from './components/views/CopagoArticleManagement';
import './index.css';
import CardQueue from './components/views/CardQueue';
import PrinterOverview from './components/views/PrinterOverview';
import EmergencyPrintMode from './components/views/EmergencyPrintMode';
import PrintserverConfig from './components/views/PrintserverConfig';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { AuthContextProvider } from './context/authContext';
import { ColorModeProvider, useColorMode } from './context/colorModeContext';
import { CountContextProvider } from './context/countContext';
import { CallContextProvider } from './context/callContext';
import { SocketIOProvider } from './context/socketIOContext';
import { StatisticJobNotifications } from './components/StatisticJobNotifications';
import { RequestActivityBar } from './components/RequestActivityBar';
import { VersionChecker } from './components/VersionChecker';
import { EntertainmentProvider, EntertainmentRouteDetector } from './context/entertainmentContext';
import AvailabilityManagement from './components/views/AvailabilityManagement';
import ResourceVoucherProductManagement from './components/views/ResourceVoucherProductManagement';
import RvpAvailabilityManagement from './components/views/RvpAvailabilityManagement';
import OpeningHourManagement from './components/views/OpeningHourManagement';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/de';
import VoucherCheck from './components/views/VoucherCheck';
import ConfirmBankTransferVoucherPayment from './components/views/ConfirmBankTransferVoucherPayment';
import GiftedBalanceVoucherIssue from './components/views/GiftedBalanceVoucherIssue';
import GiftedBalanceVoucherApprovals from './components/views/GiftedBalanceVoucherApprovals';
import FormsAndLinks from './components/views/FormsAndLinks';
import { preloadCriticalImages } from './util/imagePreloader';
import RoleManagement from './components/views/RoleManagement';
import RoleRightsMatrix from './components/views/RoleRightsMatrix';
import RoleRightsOverview from './components/views/RoleRightsOverview';
import VoucherProductManagement from './components/views/VoucherProductManagement';
import ResourceManagement from './components/views/ResourceManagement';
import MailTemplateManagement from './components/views/MailTemplateManagement';
import MailTemplateEditor from './components/views/MailTemplateEditor';
import KnowledgeBaseManagement from './components/views/KnowledgeBaseManagement';
import Settings from './components/views/Settings';
import TimebutlerUserConversion from './components/views/TimebutlerUserConversion';
import DepartmentRoleMappingManagement from './components/views/DepartmentRoleMappingManagement';
import SpecialDayManagement from './components/views/SpecialDayManagement';
import DiscountCodeManagement from './components/views/DiscountCodeManagement';
import EmployeeDiscountCodes from './components/views/EmployeeDiscountCodes';
import DiscountCodeStatistics from './components/views/DiscountCodeStatistics';
import FreeDrinks from './components/views/FreeDrinks';
import FreeDrinksStatistics from './components/views/FreeDrinksStatistics';
import EmployeeStatus from './components/views/EmployeeStatus';
import EmployeeCardStockManagement from './components/views/EmployeeCardStockManagement';
import BonStats from './components/views/BonStats';
import BonRevenue from './components/views/BonRevenue';
import InvoiceManagement from './components/views/InvoiceManagement';
import BonList from './components/views/BonList';
import SpecialSyncImport from './components/views/SpecialSyncImport';
import NewsManagement from './components/views/NewsManagement';
import Operator from './components/views/Operator';
import EmergencyTotpSetup from './components/views/EmergencyTotpSetup';
import EscalatedChatsView from './components/views/EscalatedChatsView';
import AgentSessionStatistics from './components/views/AgentSessionStatistics';
import CopagoCashbook from './components/views/CopagoCashbook';
import VoucherMonthlySummary from './components/views/VoucherMonthlySummary';
import VoucherStatistics from './components/views/VoucherStatistics';
import VoucherRedemptionHistory from './components/views/VoucherRedemptionHistory';
import CompetitorAvailability from './components/views/CompetitorAvailability';
import MailFactoryView from './components/views/MailFactoryView';
import DevMailFactoryView from './components/views/DevMailFactoryView';
import LasertagStatistics from './components/views/LasertagStatistics';
import TimeMachineView from './components/views/TimeMachineView';
import AvailabilityDiagnosticsView from './components/views/AvailabilityDiagnosticsView';
import ExperimentsView from './components/views/ExperimentsView';
import AgentReportedConversationsView from './components/views/AgentReportedConversationsView';
import AgentChatAuditView from './components/views/AgentChatAuditView';
import SelfServiceStatistics from './components/views/SelfServiceStatistics';
import CallCenterStatistics from './components/views/CallCenterStatistics';
import { StatisticPageLoadingSkeleton } from './components/ui/StatisticPageSkeleton';
import LazyRouteErrorBoundary, { LazyRouteLoadSuccess } from './components/ui/LazyRouteErrorBoundary';

const DemographyStatistics = lazy(() => import('./components/views/DemographyStatistics'));
const BookingOriginStatistics = lazy(() => import('./components/views/BookingOriginStatistics'));
const BookingWidgetConversionAnalytics = lazy(() =>
  import('./components/views/BookingWidgetConversionAnalytics')
);
const CustomerJourneyAnalytics = lazy(() =>
  import('./components/views/CustomerJourneyAnalytics')
);
const MarketingCostStatistics = lazy(() =>
  import('./components/views/MarketingCostStatistics')
);
const MarketingCostManagement = lazy(() =>
  import('./components/views/MarketingCostManagement')
);
const UpgradeStatistics = lazy(() => import('./components/views/UpgradeStatistics'));
const UpcomingUpgradeBookingOverview = lazy(() =>
  import('./components/views/UpcomingUpgradeBookingOverview')
);
import PendingBookingsView from './components/views/PendingBookingsView';
import TestBookingCleanupView from './components/views/TestBookingCleanupView';
import FeedbackView from './components/views/FeedbackView';
import FeedbackAdminView from './components/views/FeedbackAdminView';
import CustomerExperienceFeedbackView from './components/views/CustomerExperienceFeedbackView';
import GoogleReviewsView from './components/views/GoogleReviewsView';
import GoogleOpeningHours from './components/views/GoogleOpeningHours';
import TemporaryReservationCleanup from './components/views/TemporaryReservationCleanup';
import CapacityCacheCleanup from './components/views/CapacityCacheCleanup';
import SwitchIdentity from './components/views/SwitchIdentity';
import RegiondoMigration from './components/views/RegiondoMigration.jsx';
import RegiondoVoucherMigration from './components/views/RegiondoVoucherMigration.jsx';
import RegiondoValueVoucherImport from './components/views/RegiondoValueVoucherImport.jsx';
import OldBookingImport from './components/views/OldBookingImport.jsx';
import AuditLogView from './components/views/AuditLogView';
import SuspiciousRequestAuditView from './components/views/SuspiciousRequestAuditView';
import BulkCommunicationView from './components/views/BulkCommunicationView';
import AirKeyManagement from './components/views/AirKeyManagement';
import AirKeyBranchMappings from './components/views/AirKeyBranchMappings';
import FleetOverview from './components/views/FleetOverview';
import DeviceTypeRequests from './components/views/DeviceTypeRequests';
import ClusterHealth from './components/views/ClusterHealth';
import MemberspotTrainingManagement from './components/views/MemberspotTrainingManagement';
import MemberspotTrainingStatus from './components/views/MemberspotTrainingStatus';
import MyTrainings from './components/views/MyTrainings';
import RecruiteeManagement from './components/views/RecruiteeManagement';

const isDevEnvironment = Boolean(import.meta.env?.DEV) || import.meta.env?.VITE_IS_DEV === 'true';

const devRoutes = isDevEnvironment
  ? [
      {
        path: 'mail-factory',
        element: <DevMailFactoryView />,
      },
    ]
  : [];

const createLazyStatisticRouteElement = (Component, routeKey) => (
  <LazyRouteErrorBoundary routeKey={routeKey}>
    <Suspense fallback={<StatisticPageLoadingSkeleton />}>
      <Component />
      <LazyRouteLoadSuccess routeKey={routeKey} />
    </Suspense>
  </LazyRouteErrorBoundary>
);

const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <EntertainmentRouteDetector>
        <RootLayout />
      </EntertainmentRouteDetector>
    ),
    children: [
      {
        index: true,
        element: <Login />,
      },
      {
        path: 'login',
        element: <Login />,
      },
      {
        path: 'rfid-login',
        element: <RfidLogin />,
      },
      {
        path: 'request-password-reset',
        element: <RequestPasswordReset />,
      },
      {
        path: 'reset-password',
        element: <ResetPassword />,
      },
      {
        element: <RequireAuth />,
        children: [
          {
            path: 'welcome',
            element: <Welcome />,
          },
          {
            path: 'pending-bookings',
            element: <PendingBookingsView />,
          },
          {
            path: 'test-booking-cleanup',
            element: <TestBookingCleanupView />,
          },
          {
            path: 'settings',
            element: <Settings />,
          },
          {
            path: 'emergencyTotp',
            element: <EmergencyTotpSetup />,
          },
          {
            path: 'calls',
            element: <Calls />,
          },
          {
            path: 'waivers',
            element: <Waivers />,
          },
          {
            path: 'bookings',
            element: <Bookings />,
          },
          {
            // Merged kitchen overview (pizza bookings + live food orders).
            path: 'kitchen',
            element: <PizzaView />,
          },
          {
            // Old pizza route kept as a redirect so existing links/bookmarks work.
            path: 'pizza',
            element: <Navigate to='/kitchen' replace />,
          },
          {
            path: 'temporaryReservationCleanup',
            element: <TemporaryReservationCleanup />,
          },
          {
            path: 'capacityCacheCleanup',
            element: <CapacityCacheCleanup />,
          },
          {
            path: 'bookingSearch',
            element: <BookingSearchView />,
          },
          {
            path: 'resources',
            element: <Resources />,
          },
          {
            path: 'smsLog',
            element: <SMS />,
          },
          {
            path: 'stats',
            element: <Statistics />,
          },
          {
            path: 'cardQueue',
            element: <CardQueue />,
          },
          {
            path: 'employeeCardStock',
            element: <EmployeeCardStockManagement />,
          },
          {
            path: 'printerOverview',
            element: <PrinterOverview />,
          },
          {
            path: 'printserverConfig',
            element: <PrintserverConfig />,
          },
          {
            path: 'emergencyPrintMode',
            element: <EmergencyPrintMode />,
          },
          {
            path: 'voucherCheck',
            element: <VoucherCheck />,
          },
          {
            path: 'voucherSearch',
            element: <VoucherSearch />,
          },
          {
            path: 'sellVoucher',
            element: <ConfirmBankTransferVoucherPayment />,
          },
          {
            path: 'giftedBalanceVoucher',
            element: <GiftedBalanceVoucherIssue />,
          },
          {
            path: 'giftedBalanceVoucherApprovals',
            element: <GiftedBalanceVoucherApprovals />,
          },
          {
            path: 'voucherStatistics',
            element: <VoucherStatistics />,
          },
          {
            path: 'voucherRedemptionHistory',
            element: <VoucherRedemptionHistory />,
          },
          {
            path: 'upgradeStatistics',
            element: createLazyStatisticRouteElement(UpgradeStatistics, 'upgradeStatistics'),
          },
          {
            path: 'upcomingUpgradeBookings',
            element: createLazyStatisticRouteElement(
              UpcomingUpgradeBookingOverview,
              'upcomingUpgradeBookings'
            ),
          },
          {
            path: 'competitors',
            element: <CompetitorAvailability />,
          },
          {
            path: 'selfServiceStats',
            element: <SelfServiceStatistics />,
          },
          {
            path: 'callCenterStats',
            element: <CallCenterStatistics />,
          },
          {
            path: 'demographyStats',
            element: createLazyStatisticRouteElement(DemographyStatistics, 'demographyStats'),
          },
          {
            path: 'bookingOriginStats',
            element: createLazyStatisticRouteElement(BookingOriginStatistics, 'bookingOriginStats'),
          },
          {
            path: 'widgetConversionAnalytics',
            element: createLazyStatisticRouteElement(
              BookingWidgetConversionAnalytics,
              'widgetConversionAnalytics'
            ),
          },
          {
            path: 'customerJourney',
            element: createLazyStatisticRouteElement(
              CustomerJourneyAnalytics,
              'customerJourney'
            ),
          },
          {
            path: 'marketingCostStatistics',
            element: createLazyStatisticRouteElement(
              MarketingCostStatistics,
              'marketingCostStatistics'
            ),
          },
          {
            // The old bespoke Google Marketing (Ads) page was replaced by the
            // marketing-cost statistic; keep old bookmarks working.
            path: 'googleMarketing',
            element: <Navigate to='/marketingCostStatistics' replace />,
          },
          {
            path: 'marketingCosts',
            element: createLazyStatisticRouteElement(
              MarketingCostManagement,
              'marketingCosts'
            ),
          },
          {
            path: 'briefings',
            element: <BriefingView />,
          },
          {
            path: 'mails',
            element: <EmailView />,
          },
          {
            path: 'customerManagement',
            element: <CustomerManagement />,
          },
          {
            path: 'userManagement',
            element: <UserManagement />,
          },
          {
            path: 'memberManagement',
            element: <MemberManagement />,
          },
          {
            path: 'playerNameBlacklist',
            element: <PlayerNameBlacklistManagement />,
          },
          {
            path: 'timebutlerConversion',
            element: <TimebutlerUserConversion />,
          },
          {
            path: 'specialSyncImport',
            element: <SpecialSyncImport />,
          },
          {
            path: 'roleManagement',
            element: <RoleManagement />,
          },
          {
            path: 'departmentRoleMapping',
            element: <DepartmentRoleMappingManagement />,
          },
          {
            path: 'roleRightsMatrix',
            element: <RoleRightsMatrix />,
          },
          {
            path: 'roleRightsOverview',
            element: <RoleRightsOverview />,
          },
          {
            path: 'groupManagement',
            element: <GroupManagement />,
          },
          {
            path: 'branchManagement',
            element: <BranchManagement />,
          },
          {
            path: 'legalEntityManagement',
            element: <LegalEntityManagement />,
          },
          {
            path: 'entertainmentManagement',
            element: <EntertainmentManagement />,
          },
          {
            path: 'getResponseMarketing',
            element: <GetResponseMarketingManagement />,
          },
          {
            path: 'onlineCustomers',
            element: <OnlineCustomerManagement />,
          },
          {
            path: 'entertainmentTypeManagement',
            element: <EntertainmentTypeManagement />,
          },
          {
            path: 'productManagement',
            element: <ProductManagement />,
          },
          {
            path: 'priceOverview',
            element: <PriceOverview />,
          },
          {
            path: 'copagoGroupManagement',
            element: <CopagoGroupManagement />,
          },
          {
            path: 'copagoArticleManagement',
            element: <CopagoArticleManagement />,
          },
          {
            path: 'bookingWidgetManagement',
            element: <BookingWidgetManagement />,
          },
          {
            path: 'resourceManagement',
            element: <ResourceManagement />,
          },
          {
            path: 'voucherProductManagement',
            element: <VoucherProductManagement />,
          },
          {
            path: 'mailTemplateManagement',
            element: <MailTemplateManagement />,
          },
          {
            path: 'knowledgeBaseManagement',
            element: <KnowledgeBaseManagement />,
          },
          {
            path: 'mailTemplateEditor',
            element: <MailTemplateEditor />,
          },
          {
            path: 'availabilityManagement',
            element: <AvailabilityManagement />,
          },
          {
            path: 'resourceVoucherProductManagement',
            element: <ResourceVoucherProductManagement />,
          },
          {
            path: 'resourceVoucherAvailabilityManagement',
            element: <RvpAvailabilityManagement />,
          },
          {
            path: 'upgradeManagement',
            element: <UpgradeManagement />,
          },
          {
            path: 'menuItems',
            element: <MenuItems />,
          },
          {
            path: 'openingHourManagement',
            element: <OpeningHourManagement />,
          },
          {
            path: 'specialDayManagement',
            element: <SpecialDayManagement />,
          },
          {
            path: 'discountCodeManagement',
            element: <DiscountCodeManagement />,
          },
          {
            path: 'employeeDiscountCodes',
            element: <EmployeeDiscountCodes />,
          },
          {
            path: 'discountCodeStatistics',
            element: <DiscountCodeStatistics />,
          },
          {
            path: 'employeeStatus',
            element: <EmployeeStatus />,
          },
          {
            path: 'formsAndLinks',
            element: <FormsAndLinks />,
          },
          {
            path: 'bonStats',
            element: <BonStats />,
          },
          {
            path: 'bonRevenue',
            element: <BonRevenue />,
          },
          {
            path: 'bonList',
            element: <BonList />,
          },
          {
            path: 'invoiceManagement',
            element: <InvoiceManagement />,
          },
          {
            path: 'storedRequests',
            element: <StoredRequestsView />,
          },
          {
            path: 'memberspotTrainingManagement',
            element: <MemberspotTrainingManagement />,
          },
          {
            path: 'memberspotTrainingStatus',
            element: <MemberspotTrainingStatus />,
          },
          {
            path: 'recruitee',
            element: <RecruiteeManagement />,
          },
          {
            path: 'myTrainings',
            element: <MyTrainings />,
          },
          {
            path: 'newsManagement',
            element: <NewsManagement />,
          },
          {
            path: 'operator',
            element: <Operator />,
          },
          {
            path: 'escalated-chats',
            element: <EscalatedChatsView />,
          },
          {
            path: 'agent-session-stats',
            element: <AgentSessionStatistics />,
          },
          {
            path: 'agent-reported',
            element: <AgentReportedConversationsView />,
          },
          {
            path: 'agent-chat-audit',
            element: <AgentChatAuditView />,
          },
          {
            path: 'lasertagStats',
            element: <LasertagStatistics />,
          },
          {
            path: 'copagoCashbook',
            element: <CopagoCashbook />,
          },
          {
            path: 'voucherMonthlySummary',
            element: <VoucherMonthlySummary />,
          },
          {
            path: 'transactional-mail-factory',
            element: <MailFactoryView />,
          },
          {
            path: 'time-machine',
            element: <TimeMachineView />,
          },
          {
            path: 'dev/availability-diagnostics',
            element: <AvailabilityDiagnosticsView />,
          },
          {
            path: 'experiments',
            element: <ExperimentsView />,
          },
          ...devRoutes,
          {
            path: 'switch-identity',
            element: <SwitchIdentity />,
          },
          {
            path: 'regiondo-migration',
            element: <RegiondoMigration />,
          },
          {
            path: 'regiondo-voucher-migration',
            element: <RegiondoVoucherMigration />,
          },
          {
            path: 'regiondo-value-voucher-import',
            element: <RegiondoValueVoucherImport />,
          },
          {
            path: 'old-booking-import',
            element: <OldBookingImport />,
          },
          {
            path: 'feedback',
            element: <FeedbackView />,
          },
          {
            path: 'feedback-admin',
            element: <FeedbackAdminView />,
          },
          {
            path: 'customer-experience-feedback',
            element: <CustomerExperienceFeedbackView />,
          },
          {
            path: 'google-reviews',
            element: <GoogleReviewsView />,
          },
          {
            path: 'google-opening-hours',
            element: <GoogleOpeningHours />,
          },
          {
            path: 'auditLog',
            element: <AuditLogView />,
          },
          {
            path: 'suspiciousRequestAudit',
            element: <SuspiciousRequestAuditView />,
          },
          {
            path: 'bulk-communication',
            element: <BulkCommunicationView />,
          },
          {
            path: 'airkey',
            element: <AirKeyManagement />,
          },
          {
            path: 'airkey/mappings',
            element: <AirKeyBranchMappings />,
          },
          {
            path: 'fleet',
            element: <FleetOverview />,
          },
          {
            path: 'deviceTypeRequests',
            element: <DeviceTypeRequests />,
          },
          {
            path: 'cluster-health',
            element: <ClusterHealth />,
          },
          {
            path: 'freeDrinks',
            element: <FreeDrinks />,
          },
          {
            path: 'freeDrinksStatistics',
            element: <FreeDrinksStatistics />,
          },
        ],
      },
    ],
  },
]);

// Preload critical images when the app starts
preloadCriticalImages();

function ThemedToastContainer() {
  const { resolvedPaletteMode } = useColorMode();
  return <ToastContainer theme={resolvedPaletteMode === 'dark' ? 'dark' : 'light'} />;
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <CountContextProvider>
      <AuthContextProvider>
        <ColorModeProvider>
          <SocketIOProvider>
            <CallContextProvider>
              <EntertainmentProvider>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='de'>
                  <StatisticJobNotifications />
                  <RequestActivityBar />
                  <VersionChecker />
                  <RouterProvider router={router} />
                </LocalizationProvider>
              </EntertainmentProvider>
            </CallContextProvider>
          </SocketIOProvider>
          <ThemedToastContainer />
        </ColorModeProvider>
      </AuthContextProvider>
    </CountContextProvider>
  </React.StrictMode>
);
